import { app, globalMatrix, IReference, IReferenceChange, matrixSession } from "../../../globals";
import { HTMLCleaner } from "../../matrixlib/index";
import { IBaseControlOptions, BaseControl } from "./BaseControl";
import { ml } from "./../../matrixlib";
import {
    IRiskConfig,
    IRiskConfigFactorWeightValue,
    IRiskConfigFactor,
    IRiskConfigFactorWeight,
    IRiskConfigReduction,
    IRiskConfigSelectChanges,
} from "../../../ProjectSettings";
import { IRiskTableParams } from "../../../client/plugins/RiskControlFolder";
import { UIToolsConstants } from "../../matrixlib/MatrixLibInterfaces";
import { ColumnEditor, FieldHandlerFactory, ITableControlOptionsColumn } from "../../businesslogic";
import { FieldDescriptions } from "../../businesslogic/FieldDescriptions";
import { GenericFieldHandler } from "../../businesslogic/FieldHandlers/GenericFieldHandler";
import { ControlState } from "../../../types";

export type {
    IRiskControlOptions,
    IRiskParameter,
    IRiskValue,
    IRiskValueFactor,
    IRiskValueFactorWeight,
    IRiskValueMitigation,
    IRiskValueMitigationChange,
    IRiskValueMap,
    IRiskRender,
};
export { RiskCalculator, RiskControlImpl };

interface IRiskControlOptions extends IBaseControlOptions {
    controlState?: ControlState;
    canEdit?: boolean;
    help?: string;
    fieldValue?: string;
    // TODO: MATRIX-7555: lint errors should be fixed for next line
    // eslint-disable-next-line
    valueChanged?: Function;
    parameter?:
        | IRiskParameter
        | IRiskTableParams /* parameters can be different if render as a table or as a control.  */;
    links?: IReference[];
    hideReadonlyColumns?: boolean; // for rendering risks in tables
}

interface IRiskParameter {
    reviewMode?: boolean;
    riskConfig?: IRiskConfig;
    showAttributeNames?: boolean; // if set to true shows attributes of the attributes for selection printing
    forceAfterWeightsInPrint?: boolean; // if true shows weights after controls in RBM
    hide_UI?: boolean; // if set to true don't render
}

interface IRiskValue {
    factors: IRiskValueFactor[];
    mitigations: IRiskValueMitigation[];
    postWeights?: IRiskValueFactorWeight[];
}
interface IRiskValueFactor {
    label: string;
    type: string;
    value: string;
    inputType?: string;
    weights: IRiskValueFactorWeight[];
}
interface IRiskValueFactorWeight {
    description: string;
    label: string;
    type: string;
    value: number;
}
interface IRiskValueMitigation {
    to: string;
    title: string;
    changes?: IRiskValueMitigationChange[];
}
interface IRiskValueMitigationChange {
    by: number;
    changes: string;
    description: string;
    name: string;
}
interface IRiskValueMap {
    [key: string]: number;
}

interface IRiskRender {
    text: string;
    foregroundColor: string;
    backgroundColor: string;
    css: string;
}

// TODO: MATRIX-7555: lint errors should be fixed for next line
// eslint-disable-next-line
$.fn.riskCtrl2 = function (this: JQuery, options: IRiskControlOptions) {
    if (!options.fieldHandler) {
        options.fieldHandler = FieldHandlerFactory.CreateHandler(
            globalMatrix.ItemConfig,
            FieldDescriptions.Field_risk2,
            options,
        );
        options.fieldHandler.initData(JSON.stringify(options.fieldValue));
    }

    let baseControl = new RiskControlImpl(this, options.fieldHandler as GenericFieldHandler);
    // TODO: MATRIX-7555: lint errors should be fixed for next line
    // eslint-disable-next-line
    this.getController = () => {
        return baseControl;
    };
    baseControl.init(options);
    return this;
};

class RiskCalculator {
    // @ts-ignore TODO: MATRIX-6934: nullStrictCheck should be fixed for next line
    private riskValue: IRiskValue;
    private config: IRiskConfig;

    constructor(config: IRiskConfig) {
        this.config = config;
    }

    /* read the value from the json string */
    // TODO: MATRIX-7555: lint errors should be fixed for next line
    // eslint-disable-next-line
    parse(fieldValue: string) {
        if (typeof fieldValue === "undefined" || fieldValue === "") {
            // @ts-ignore TODO: MATRIX-6934: nullStrictCheck should be fixed for next line
            const factors = this.config.factors.map((factor) => {
                let defaultValue = "";
                let weightsSetByChangeConfig: Record<string, string | number> = {};
                if (factor.options && factor.options.length > 0) {
                    const optionDefault = factor.options[0];
                    defaultValue = optionDefault.value;
                    optionDefault.changes.forEach((change) => {
                        if (change.changesWeight) {
                            weightsSetByChangeConfig[change.changesWeight] = change.value;
                        }
                    });
                }
                const weightValues = factor.weights.map((weight) => {
                    let label = "";
                    let value = 1;
                    if (weight.values.length > 0) {
                        label = RiskCalculator.labelDisplay(weight.values[0]);
                        value = weight.values[0].factor;
                        if (weightsSetByChangeConfig[weight.type]) {
                            const activeValue = weight.values.filter(
                                // TODO: MATRIX-7555: lint errors should be fixed for next line
                                // eslint-disable-next-line
                                (value) => value.factor == weightsSetByChangeConfig[weight.type],
                            );
                            if (activeValue.length > 0) {
                                const matching = activeValue[0];
                                label = RiskCalculator.labelDisplay(matching);
                                value = matching.factor;
                            }
                        }
                    }
                    return {
                        type: weight.type,
                        label: weight.label,
                        description: label,
                        value: value,
                    };
                });
                return {
                    label: factor.label,
                    type: factor.type,
                    value: defaultValue,
                    inputType: factor.inputType,
                    weights: weightValues,
                };
            });
            this.riskValue = { factors: factors, mitigations: [] };
        } else {
            this.riskValue = JSON.parse(fieldValue);
            if (!this.riskValue.mitigations) {
                this.riskValue.mitigations = [];
            }
            if (!this.riskValue.factors) {
                this.riskValue.factors = [];
            }
        }

        // replace weird  &dash, with a simple -
        // the dash is in use in versions before 1.6
        if (this.riskValue.factors) {
            $.each(this.riskValue.factors, function (factorIdx, factor) {
                if (factor.weights) {
                    $.each(factor.weights, function (weightIdx, weight) {
                        weight.description = weight.description ? weight.description.replace(/[\u2010]/g, "-") : "";
                    });
                }
            });
        }
    }

    /* remove missing downlinks */
    // TODO: MATRIX-7555: lint errors should be fixed for next line
    // eslint-disable-next-line
    updateMitigations(possibleRefs: IReference[]) {
        let possibleLinks = possibleRefs ? possibleRefs.map((link) => link.to) : [];
        let mitigationsBefore = this.riskValue.mitigations.length;
        this.riskValue.mitigations = this.riskValue.mitigations.filter(
            // TODO: MATRIX-7555: lint errors should be fixed for next line
            // eslint-disable-next-line
            (mitigation) => possibleLinks.indexOf(mitigation.to) != -1,
        );
        // TODO: MATRIX-7555: lint errors should be fixed for next line
        // eslint-disable-next-line
        return mitigationsBefore != this.riskValue.mitigations.length;
    }

    /* remove missing downlinks */
    // TODO: MATRIX-7555: lint errors should be fixed for next line
    // eslint-disable-next-line
    updateMitigationTitles(possibleLinks: IReference[]) {
        if (!possibleLinks) {
            possibleLinks = [];
        }
        // mitigations are just normal downlinks in the project. the json definition of the risk saves additional meta info
        // for each downlink (like what is mitigated how much). In case the downlink does not exist anymore the mitigation is removed.
        // In case the title of the mitigated item was updated the new title is used
        for (let idx = this.riskValue.mitigations.length - 1; idx >= 0; idx--) {
            // verify if the reference item still exists (here we rely on the item refs from the server)
            // if the reference still exists, make sure the title is the current title, if not delete the mitigation
            let mitTitle = "";
            for (let refs = 0; refs < possibleLinks.length; refs++) {
                if (possibleLinks[refs].to === this.riskValue.mitigations[idx].to) {
                    mitTitle = possibleLinks[refs].title;
                }
            }
            if (mitTitle) {
                this.riskValue.mitigations[idx].title = mitTitle;

                if (this.riskValue.mitigations[idx].changes) {
                    $.each(this.riskValue.mitigations[idx].changes, function (changeIdx, change) {
                        if (!change["changes"]) {
                            change["changes"] = "";
                        }
                    });
                }
            } else {
                // does not exist remove it. someone deleted the item
                this.riskValue.mitigations.splice(idx, 1);
            }
        }
    }

    // TODO: MATRIX-7555: lint errors should be fixed for next line
    // eslint-disable-next-line
    init(riskValue: IRiskValue) {
        this.riskValue = ml.JSON.clone({ ...{}, ...riskValue }); // copy/clone the value
    }

    /** get the value */
    // TODO: MATRIX-7555: lint errors should be fixed for next line
    // eslint-disable-next-line
    getValue() {
        return this.riskValue; // copy/clone the value
    }

    // get one attribute (factor, weight, ram, rbm, ... ) as html blob
    getAttributeHTML(attributeIn: string): string {
        let attributeLower = attributeIn.toLowerCase();
        // all after first dot
        let attr = attributeIn.substr(attributeIn.indexOf(".") + 1);
        let attrLower = attr.toLowerCase();

        // TODO: MATRIX-7555: lint errors should be fixed for next line
        // eslint-disable-next-line
        if (attributeLower.indexOf("before.") == 0) {
            let rbm = this.getRBM();
            let rbmLower: Record<string, number> = {};
            for (let p in rbm) {
                rbmLower[p.toLowerCase()] = rbm[p];
            }
            // TODO: MATRIX-7555: lint errors should be fixed for next line
            // eslint-disable-next-line
            if (rbm[attr] != undefined) {
                return `<span class="${attributeLower}">${rbm[attr]}</span>`;
                // TODO: MATRIX-7555: lint errors should be fixed for next line
                // eslint-disable-next-line
            } else if (rbmLower[attrLower] != undefined) {
                return `<span class="${attributeLower}">${rbmLower[attrLower]}</span>`;
            } else {
                return "unset attribute '" + attributeLower + "'";
            }
            // TODO: MATRIX-7555: lint errors should be fixed for next line
            // eslint-disable-next-line
        } else if (attributeLower.indexOf("after.") == 0) {
            let rbm = this.getRBM();
            let ram = this.getRAM(rbm);
            let ramLower: Record<string, number> = {};
            for (let p in ram) {
                ramLower[p.toLowerCase()] = ram[p];
            }
            // TODO: MATRIX-7555: lint errors should be fixed for next line
            // eslint-disable-next-line
            if (ram[attr] != undefined) {
                return `<span class="${attributeLower}">${ram[attr]}</span>`;
                // TODO: MATRIX-7555: lint errors should be fixed for next line
                // eslint-disable-next-line
            } else if (ramLower[attrLower] != undefined) {
                return `<span class="${attributeLower}">${ram[attrLower]}</span>`;
            } else {
                return "unset attribute '" + attributeLower + "'";
            }
            // TODO: MATRIX-7555: lint errors should be fixed for next line
            // eslint-disable-next-line
        } else if (attributeLower == "totalrbm") {
            return this.getRiskSumSpan(this.getRBM());
            // TODO: MATRIX-7555: lint errors should be fixed for next line
            // eslint-disable-next-line
        } else if (attributeLower == "totalram") {
            let rbm = this.getRBM();
            return this.getRiskSumSpan(this.getRAM(rbm));
            // TODO: MATRIX-7555: lint errors should be fixed for next line
            // eslint-disable-next-line
        } else if (attributeLower == "colorbeforeforeground") {
            return this.getColor(this.getRBM(), true);
            // TODO: MATRIX-7555: lint errors should be fixed for next line
            // eslint-disable-next-line
        } else if (attributeLower == "colorbeforebackground") {
            return this.getColor(this.getRBM(), false);
            // TODO: MATRIX-7555: lint errors should be fixed for next line
            // eslint-disable-next-line
        } else if (attributeLower == "colorafterforeground") {
            let rbm = this.getRBM();
            return this.getColor(this.getRAM(rbm), true);
            // TODO: MATRIX-7555: lint errors should be fixed for next line
            // eslint-disable-next-line
        } else if (attributeLower == "colorafterbackground") {
            let rbm = this.getRBM();
            return this.getColor(this.getRAM(rbm), false);
            // TODO: MATRIX-7555: lint errors should be fixed for next line
            // eslint-disable-next-line
        } else if (attributeLower == "riskcontrols") {
            let riskControls = "<div>";
            for (let mitigation of this.riskValue.mitigations) {
                let riskControl = "<div class='riskControl'>";
                // add the hyper link
                riskControl += `<span class='riskControlLink'>_LINK_${mitigation.to}_LINK_<span> `;

                // add the description (there's no description for post reduction kind of risk controls afaik)
                if (!this.config.postReduction && mitigation.changes) {
                    // @ts-ignore TODO: MATRIX-6934: nullStrictCheck should be fixed for next line
                    for (let reduction of this.config.reductions) {
                        for (let change of mitigation.changes) {
                            // TODO: MATRIX-7555: lint errors should be fixed for next line
                            // eslint-disable-next-line
                            if (change.name == reduction.name) {
                                let chg = `<span class="riskControlChange">${change.description}!</span> `;
                                riskControl += chg;
                            }
                        }
                    }
                }
                // if it is a mitigation table there might be some more fields
                if (this.config.mitigationTable) {
                    // but we don't actually print those...
                }
                riskControl += "</div>";
                riskControls += riskControl;
            }

            riskControls += "</div>";
            return riskControls;
        } else {
            // a factor
            // TODO: MATRIX-7555: lint errors should be fixed for next line
            // eslint-disable-next-line
            let factors = this.riskValue.factors.filter((factor) => factor.type.toLowerCase() == attributeLower);
            if (factors.length) {
                // @ts-ignore TODO: MATRIX-6934: nullStrictCheck should be fixed for next line
                // TODO: MATRIX-7555: lint errors should be fixed for next line
                // eslint-disable-next-line
                let factorsConfig = this.config.factors.filter((factor) => factor.type == factors[0].type);
                let text = factors[0].value;
                let style = "";
                if (factorsConfig.length) {
                    // TODO: MATRIX-7555: lint errors should be fixed for next line
                    // eslint-disable-next-line
                    if (factorsConfig[0].inputType == "textarea") {
                        style = "white-space:pre-wrap";
                        // TODO: MATRIX-7555: lint errors should be fixed for next line
                        // eslint-disable-next-line
                    } else if (factorsConfig[0].inputType == "select") {
                        // @ts-ignore TODO: MATRIX-6934: nullStrictCheck should be fixed for next line
                        // TODO: MATRIX-7555: lint errors should be fixed for next line
                        // eslint-disable-next-line
                        let opts = factorsConfig[0].options.filter((option) => option.value == text);
                        if (opts.length) {
                            text = opts[0].label;
                        }
                    }
                }
                return `<span class="${factors[0].type}" style="${style}">${text ? text : ""}</span>`;
            }
            return "unknown factor '" + attributeLower + "'";
        }
    }

    /* get a weight before risk controls */
    // TODO: MATRIX-7555: lint errors should be fixed for next line
    // eslint-disable-next-line
    getWeight(factorType: string, weightType: string) {
        // TODO: MATRIX-7555: lint errors should be fixed for next line
        // eslint-disable-next-line
        let factors = this.riskValue.factors.filter((factor) => factor.type == factorType);
        if (!factors.length) {
            return 0;
        }
        // TODO: MATRIX-7555: lint errors should be fixed for next line
        // eslint-disable-next-line
        let weights = factors[0].weights.filter((weight) => weight.type == weightType);
        if (!weights.length) {
            return 0;
        }
        return weights[0].value;
    }
    /* get a weights before risk controls */
    // TODO: MATRIX-7555: lint errors should be fixed for next line
    // eslint-disable-next-line
    getRBM() {
        let that = this;

        let rbm: IRiskValueMap = {};
        $.each(this.config.factors, function (factorIdx, factor) {
            $.each(factor.weights, function (weightIdx, weight) {
                rbm[weight.type] = that.getWeight(factor.type, weight.type);
            });
        });
        return rbm;
    }
    /* get weights after risk controls by substracting something for each risk control*/
    getRAMByMath(rbm: IRiskValueMap): IRiskValueMap {
        let that = this;

        let ram = ml.JSON.clone(rbm);

        $.each(that.riskValue.mitigations, function (mitigationIdx, mitigation) {
            if (that.config.mitigationTable) {
                $.each(mitigation.changes, function (ccidx, cc) {
                    ram[cc.changes] += cc.by;
                });
            } else {
                $.each(mitigation.changes, function (changeIdx, change) {
                    // add it up
                    ram[change.changes] += change.by;
                });
            }
            $.each(that.config.factors, function (factorIdx, factor) {
                $.each(factor.weights, function (weightIdx, weight) {
                    // make sure ram is not bigger or smaller than possible range
                    let minVal = 20000;
                    let maxVal = -20000;

                    $.each(weight.values, function (valIdx, val) {
                        minVal = Math.min(minVal, val.factor);
                        maxVal = Math.max(maxVal, val.factor);
                    });

                    ram[weight.type] = Math.max(ram[weight.type], minVal);
                    ram[weight.type] = Math.min(ram[weight.type], maxVal);
                });
            });
        });

        return ram;
    }

    /* get weights set by user */
    getRAMByUser(rbm: IRiskValueMap): IRiskValueMap {
        let that = this;

        let ram = ml.JSON.clone(rbm);

        // @ts-ignore TODO: MATRIX-6934: nullStrictCheck should be fixed for next line
        if (that.riskValue.mitigations.length === 0 && this.config.mitigationTypes.length > 0) {
            // no mitigations, mean the risks before and after are the same
            return ram;
        }

        // @ts-ignore TODO: MATRIX-6934: nullStrictCheck should be fixed for next line
        $.each(this.config.postReduction.weights, function (weightIdx, weight) {
            // @ts-ignore TODO: MATRIX-6934: nullStrictCheck should be fixed for next line
            // TODO: MATRIX-7555: lint errors should be fixed for next line
            // eslint-disable-next-line
            let setWeight = that.riskValue.postWeights.filter((postWeight) => postWeight.type == weight.type);
            if (setWeight.length) {
                ram[weight.type] = setWeight[0].value;
            }
        });

        return ram;
    }

    getRAM(rbm: IRiskValueMap): IRiskValueMap {
        if (this.config.postReduction) {
            return this.getRAMByUser(rbm);
        } else {
            return this.getRAMByMath(rbm);
        }
    }

    // returns rendering info for ram and rbm
    // TODO: MATRIX-7555: lint errors should be fixed for next line
    // eslint-disable-next-line
    getRiskSumText(riskValues: IRiskValueMap) {
        let that = this;

        let values: number[] = [];
        $.each(that.config.factors, function (factorIdx, factor) {
            $.each(factor.weights, function (weightIdx, weight) {
                values.push(riskValues[weight.type]);
            });
        });

        if (that.config.method === "lookup") {
            let zone = "";
            let text = "unknown combination of values";
            let foreground = "red";
            let background = "transparent";

            // find same combination of input values
            $.each(that.config.rpns, function (idx, rpn) {
                let hit = true;
                $.each(that.config.factors, function (factorIdx, factor) {
                    $.each(factor.weights, function (weightIdx, weight) {
                        // compare number to string
                        // TODO: MATRIX-7555: lint errors should be fixed for next line
                        // eslint-disable-next-line
                        if (riskValues[weight.type] != rpn[weight.type]) {
                            hit = false;
                        }
                    });
                });
                if (hit) {
                    zone = rpn.zone;
                    text = rpn.text;
                    return;
                }
            });
            // get color for display
            if (zone) {
                $.each(that.config.charts, function (idx, chart) {
                    // TODO: MATRIX-7555: lint errors should be fixed for next line
                    // eslint-disable-next-line
                    if (chart.zone == zone) {
                        foreground = chart.foreground;
                        background = chart.background;
                    }
                });
            }
            return {
                text: text,
                foregroundColor: foreground,
                backgroundColor: background,
                css: "",
            };
        } else {
            let result = that.config.method === "*" ? 1 : 0;
            $.each(values, function (valIdx, val) {
                if (that.config.method === "*") {
                    result *= val;
                } else {
                    result += val;
                }
            });

            return {
                text: values.join(" " + that.config.method + " ") + " = " + Math.round(result * 100) / 100,
                foregroundColor: "",
                backgroundColor: "",
                css:
                    // @ts-ignore TODO: MATRIX-6934: nullStrictCheck should be fixed for next line
                    result <= that.config.maxGreen
                        ? "riskgreen"
                        : // @ts-ignore TODO: MATRIX-6934: nullStrictCheck should be fixed for next line
                        result <= that.config.maxYellow
                        ? "riskyellow"
                        : "riskred",
            };
        }
    }

    // returns a span with rendering info
    // TODO: MATRIX-7555: lint errors should be fixed for next line
    // eslint-disable-next-line
    getRiskSumSpan(riskValues: IRiskValueMap) {
        let riskSum = this.getRiskSumText(riskValues);
        return `<span class="${riskSum.css ? riskSum.css : ""}"  style="color:${
            riskSum.foregroundColor ? riskSum.foregroundColor : ""
        };background-color:${riskSum.backgroundColor ? riskSum.backgroundColor : ""}">${riskSum.text}</span>`;
    }

    // returns the color as text (for styling)
    // TODO: MATRIX-7555: lint errors should be fixed for next line
    // eslint-disable-next-line
    getColor(riskValues: IRiskValueMap, foreground: boolean) {
        let riskSum = this.getRiskSumText(riskValues);
        if (foreground) {
            if (riskSum.foregroundColor) {
                return riskSum.foregroundColor;
                // TODO: MATRIX-7555: lint errors should be fixed for next line
                // eslint-disable-next-line
            } else if (riskSum.css == "riskgreen") {
                return "#FFF";
                // TODO: MATRIX-7555: lint errors should be fixed for next line
                // eslint-disable-next-line
            } else if (riskSum.css == "riskyellow") {
                return "#000";
                // TODO: MATRIX-7555: lint errors should be fixed for next line
                // eslint-disable-next-line
            } else if (riskSum.css == "riskred") {
                return "#FFF";
            }
            return "";
        } else {
            if (riskSum.backgroundColor) {
                return riskSum.backgroundColor;
                // TODO: MATRIX-7555: lint errors should be fixed for next line
                // eslint-disable-next-line
            } else if (riskSum.css == "riskgreen") {
                return "#0A0";
                // TODO: MATRIX-7555: lint errors should be fixed for next line
                // eslint-disable-next-line
            } else if (riskSum.css == "riskyellow") {
                return "#FF0";
                // TODO: MATRIX-7555: lint errors should be fixed for next line
                // eslint-disable-next-line
            } else if (riskSum.css == "riskred") {
                return "#F00";
            }
            return "";
            return riskSum.backgroundColor ? riskSum.backgroundColor : "";
        }
    }

    static labelDisplay(weightValue: IRiskConfigFactorWeightValue): string {
        return weightValue.factor + (weightValue.shortname ? " - " + weightValue.shortname : "");
    }

    updateMitigationsChanges(): void {
        if (this.config.postReduction) {
            console.info(
                "Skipping updateMitigationsChanges as reductions is set for all risk controls combined instead of per risk control",
            );
            return;
        }
        let names = this.config.reductions?.map((reduction) => reduction.name) || [];
        for (let mitigation of this.riskValue.mitigations) {
            mitigation.changes = mitigation?.changes?.filter((change) => {
                let found = names.includes(change.name);
                if (!found) {
                    console.warn("Mitigation change not found in config: ", change.name);
                }
                return found;
            });
        }
    }
}

class RiskControlImpl extends BaseControl<GenericFieldHandler> {
    // @ts-ignore TODO: MATRIX-6934: nullStrictCheck should be fixed for next line
    private settings: IRiskControlOptions;
    // @ts-ignore TODO: MATRIX-6934: nullStrictCheck should be fixed for next line
    private config: IRiskConfig;
    // @ts-ignore TODO: MATRIX-6934: nullStrictCheck should be fixed for next line
    private risk: JQuery;
    // @ts-ignore TODO: MATRIX-6934: nullStrictCheck should be fixed for next line
    private mitbody: JQuery;
    // @ts-ignore TODO: MATRIX-6934: nullStrictCheck should be fixed for next line
    private isPrint: boolean;
    // @ts-ignore TODO: MATRIX-6934: nullStrictCheck should be fixed for next line
    private riskCalculator: RiskCalculator;
    // @ts-ignore TODO: MATRIX-6934: nullStrictCheck should be fixed for next line
    private mitigationsRemoved: boolean;

    private factorInputToTriggerChangeAfterLoad: JQuery[] = [];

    constructor(control: JQuery, fieldHandler: GenericFieldHandler) {
        super(control, fieldHandler);
    }

    // TODO: MATRIX-7555: lint errors should be fixed for next line
    // eslint-disable-next-line
    init(options: IRiskControlOptions) {
        let that = this;

        if (!matrixSession.hasRisks()) {
            this._root.append("risks not licensed");
            return;
        }

        if ((options?.parameter as IRiskParameter).reviewMode === true) {
            options.controlState = ControlState.Print;
        }

        let defaultOptions: IRiskControlOptions = {
            controlState: ControlState.FormView, // read only rendering
            dummyData: false, // fill control with a dummy text (for form design...)
            canEdit: false, // whether data can be edited
            // TODO: MATRIX-7555: lint errors should be fixed for next line
            // eslint-disable-next-line
            valueChanged: function () {},
            parameter: {},
        };

        this.settings = <IRiskControlOptions>ml.JSON.mergeOptions(defaultOptions, options);

        // @ts-ignore TODO: MATRIX-6934: nullStrictCheck should be fixed for next line
        if (this.settings && this.settings.parameter.hide_UI) {
            this._root.hide();
        }

        let config = (this.settings?.parameter as IRiskParameter).riskConfig
            ? (this.settings.parameter as IRiskParameter).riskConfig
            : globalMatrix.ItemConfig.getRiskConfig();

        if (!config) {
            this._root.append("<p style='color:red'>Risk configuration settings is missing</p>");
            return;
        }
        this.config = config;

        // TODO: MATRIX-7555: lint errors should be fixed for next line
        // eslint-disable-next-line
        this.isPrint = this.settings.controlState == ControlState.Print;

        // *********************************************************************************
        //  prepare the data
        //
        //  get it from JSON parameter and clean it up if necessary,
        //  e.g. if some referred mitigations do not exist anymore
        //
        // **********************************************************************************

        this.riskCalculator = new RiskCalculator(this.config);

        // @ts-ignore TODO: MATRIX-6934: nullStrictCheck should be fixed for next line
        this.riskCalculator.parse(this.settings.fieldValue);

        // @ts-ignore TODO: MATRIX-6934: nullStrictCheck should be fixed for next line
        this.mitigationsRemoved = this.riskCalculator.updateMitigations(this.settings.links);

        // @ts-ignore TODO: MATRIX-6934: nullStrictCheck should be fixed for next line
        this.riskCalculator.updateMitigationTitles(this.settings.links);

        // *********************************************************************************
        // save the original data
        // *********************************************************************************

        this._root.data("original", JSON.stringify(this.riskCalculator.getValue()));

        // *********************************************************************************
        // In case a downlink exists but without meta info, add it to the UI (will trigger save)
        // *********************************************************************************

        // Note: this can happen if someone adds a reference but does not save after)
        if (
            this.settings.isForm &&
            matrixSession.isEditor() &&
            // TODO: MATRIX-7555: lint errors should be fixed for next line
            // eslint-disable-next-line
            this.settings.controlState != ControlState.HistoryView &&
            // TODO: MATRIX-7555: lint errors should be fixed for next line
            // eslint-disable-next-line
            this.settings.controlState != ControlState.Zen
        ) {
            // @ts-ignore TODO: MATRIX-6934: nullStrictCheck should be fixed for next line
            for (let refs = 0; refs < this.settings.links.length; refs++) {
                // @ts-ignore TODO: MATRIX-6934: nullStrictCheck should be fixed for next line
                if (this.canBeMitigation(this.settings.links[refs].to)) {
                    let mitExists = false;
                    for (let idx = 0; idx < this.riskCalculator.getValue().mitigations.length; idx++) {
                        // @ts-ignore TODO: MATRIX-6934: nullStrictCheck should be fixed for next line
                        if (this.settings.links[refs].to === this.riskCalculator.getValue().mitigations[idx].to) {
                            mitExists = true;
                        }
                    }
                    if (!mitExists) {
                        ml.Logger.log("info", "found downlink, which is not a mitigation, adding it as mitigation");
                        this.riskCalculator.getValue().mitigations.push({
                            // @ts-ignore TODO: MATRIX-6934: nullStrictCheck should be fixed for next line
                            to: this.settings.links[refs].to,
                            // @ts-ignore TODO: MATRIX-6934: nullStrictCheck should be fixed for next line
                            title: this.settings.links[refs].title,
                            changes: [],
                        });
                    }
                }
            }
        }

        // *********************************************************************************
        // Check wether mitigation changes is defined in the config. If not, remove it. This will trigger a needSave
        // *********************************************************************************

        this.riskCalculator.updateMitigationsChanges();

        // *********************************************************************************
        // render the UI
        // *********************************************************************************

        if (this.settings.controlState === ControlState.Tooltip || this.isPrint) {
            this.settings.canEdit = false;
        }
        let riskBody = this._root; // in case it is rendered in table form this is a <tr>
        if (!(this.settings.parameter as IRiskTableParams).tableOptions) {
            // create the heading and a container for the risk
            this._root.append(super.createHelp(this.settings));

            let extraClass = this.shouldUseOldLayout() ? "" : "risk-label-combined";

            riskBody = $(
                `<div class='container riskTable ${extraClass} ${
                    this.settings.controlState === ControlState.Tooltip || this.settings.isHistory ? "riskCompact" : ""
                }'>`,
            );
            if (this.isPrint) {
                riskBody.addClass("printNoBox");
            }
            this._root.append(riskBody);
        }

        // *********************************************************************************
        // PRE MITIGATION
        // *********************************************************************************

        if ((this.settings.parameter as IRiskTableParams).tableOptions) {
            // *********************************************************************************
            // render as table row
            // *********************************************************************************

            // @ts-ignore TODO: MATRIX-6934: nullStrictCheck should be fixed for next line
            this.renderTableBodyRow(this.settings.id);
        } else {
            // *********************************************************************************
            // render as multi line control
            // *********************************************************************************
            let tableClass = this.isPrint ? "risk2" : "table riskTable";

            this.risk = $(`<table  class="${tableClass}">`);
            this.risk.append("<tbody>");

            $.each(this.config.factors, function (factorIdx, factor) {
                // create something like Cause: [Enter Cause] input field as first two columns in table
                that.renderFactorWithWeightsLine(factor);
            });

            let rbmLine = $("<tr>").appendTo(this.risk);
            if (this.config.rbm && this.config.rbm.hidden) {
                rbmLine.addClass("hidden");
            }
            if (this.shouldUseOldLayout()) {
                rbmLine.append($("<td>").attr("colspan", 2));
                rbmLine.append(
                    $('<td class="RBM riskReductionLabel" colspan="1">').html(
                        this.config.rbm && this.config.rbm.short ? this.config.rbm.short : "RBM",
                    ),
                );
                rbmLine.append($('<td class="RBM  totalRBM risksum riskSelect ">'));
            } else {
                let rbmTd = $("<td>").attr("colspan", 2);
                rbmLine.append($("<td>").attr("colspan", 1));
                rbmLine.append(rbmTd);
                rbmTd.append(
                    $(
                        `<span class='RBM riskReductionLabel'>${
                            this.config.rbm && this.config.rbm.short ? this.config.rbm.short : "RBM"
                        }</span>`,
                    ),
                );
                rbmTd.append($('<div class="RBM  totalRBM risksum riskSelect ">'));
            }

            riskBody.append(this.risk);
        }

        // *********************************************************************************
        // hook up changes to inputs
        // *********************************************************************************

        $("input", this._root).change(function () {
            that.riskChange();
        });
        $("textarea", this._root).change(function () {
            that.riskChange();
        });
        $(".riskrichtext", this._root).change(function () {
            that.riskChange();
        });
        $("select", this._root).change(function () {
            that.riskChange();
        });

        // *********************************************************************************
        // set values pre mitigation
        // *********************************************************************************
        $.each(this.riskCalculator.getValue().factors, function (factorIdx, factorVal) {
            let factor = <IRiskValueFactor>factorVal;
            // figure out if the input should be rendered differently
            $.each(that.config.factors, function (idx, factorType) {
                if (factorType.type === factorVal.type && factorType.inputType) {
                    factor.inputType = factorType.inputType;
                }
            });
            // this line might update risk's printed values if configuration changes
            factor.label = that.getLabelFactor(factor.type);

            // fill the input
            if (factor.inputType && factor.inputType === "select") {
                $("select[name=" + factorVal.type + "]", that._root).val(factorVal.value);
            } else if (factor.inputType && factor.inputType === "textarea") {
                let ta = $("textarea[name=" + factorVal.type + "]", that._root);
                if (ta.length) {
                    // if not some old value from previous config might exist...
                    // @ts-ignore TODO: MATRIX-6934: nullStrictCheck should be fixed for next line
                    ta.val(ml.UI.lt.forUI(factorVal.value, that.settings.fieldId));
                }
            } else if (factor.inputType && factor.inputType === "richtext") {
                let tr = $(".riskrichtext[name=" + factorVal.type + "]", that._root);
                if (tr.length) {
                    // if not some old value from previous config might exist...
                    that.setFactorRichValue(tr, factorVal.value);
                }
            } else {
                $("input[name=" + factorVal.type + "]", that._root).val(
                    // @ts-ignore TODO: MATRIX-6934: nullStrictCheck should be fixed for next line
                    ml.UI.lt.forUI(factorVal.value, that.settings.fieldId),
                );
            }

            $.each(factorVal.weights, function (weightIdx, weight) {
                // these two lines might update risk's printed values if configuration changes
                weight.label = that.getLabelWeight(weight.type);
                weight.description = weight.value + " - " + that.getLabelWeightFactor(weight.type, weight.value);

                that.setSelectValues($('select[name="' + weight.type + '"]:not(.riskAfter)', that._root), weight.value);
            });
        });

        // *********************************************************************************
        // MITIGATION AREA
        // *********************************************************************************

        if ((this.settings.parameter as IRiskTableParams).tableOptions) {
            // has been added in table already
        } else {
            // create control container
            this.mitbody = $("<div>");
            this.risk.append($("<tr>").append($('<td colspan=4 style="border:none">').append(this.mitbody)));
            // add details
            this.renderMitigationSelect(false);
        }

        // *********************************************************************************
        // POST MITIGATION
        // *********************************************************************************

        if ((this.settings.parameter as IRiskTableParams).tableOptions) {
            // *********************************************************************************
            // init post mitigation factors (if displayed as table)
            // *********************************************************************************

            if (!this.riskCalculator.getValue().postWeights) {
                this.riskCalculator.getValue().postWeights = [];
            }

            $.each(this.config.factors, function (factorIdx, factor) {
                $.each(factor.weights, function (weightIdx, weight) {
                    let select = $("select[name='" + weight.type + "'].riskAfter", that._root);

                    select.change(function () {
                        // store the new value
                        let exists = false;
                        let selectedOption = $(":selected", select);
                        let description = selectedOption.data("description");
                        let value = <number>selectedOption.data("value");
                        $.each(that.riskCalculator.getValue().postWeights, function (pwidx, pwi) {
                            if (pwi.type === weight.type) {
                                exists = true;
                                pwi.description = description;
                                pwi.value = value;
                            }
                        });
                        if (!exists) {
                            // @ts-ignore TODO: MATRIX-6934: nullStrictCheck should be fixed for next line
                            that.riskCalculator.getValue().postWeights.push({
                                type: weight.type,
                                value: value,
                                description: description,
                                label: weight.label,
                            });
                        }
                        // update the display
                        that.riskChange();
                    });

                    // default value for post mitigation is the before mitigation
                    let sameBefore = $('select[name="' + weight.type + '"].riskBefore', that._root);
                    let selectValue = <number>$(":selected", sameBefore).data("value");

                    // check if actual value has been saved before and overwrite the default
                    $.each(that.riskCalculator.getValue().postWeights, function (pwidx, pwi) {
                        if (pwi.type === weight.type) {
                            selectValue = pwi.value;
                        }
                    });

                    $("option", select).each(function (optIdx, opt) {
                        // TODO: MATRIX-7555: lint errors should be fixed for next line
                        // eslint-disable-next-line
                        if ($(opt).data("value") == selectValue) {
                            $(opt).prop("selected", true);
                        }
                    });

                    // update the description text to match the dropdown
                    $.each(that.riskCalculator.getValue().postWeights, function (pwidx, pwi) {
                        if (pwi.type === weight.type) {
                            let selectedOption = $(":selected", select);
                            let description = selectedOption.data("description");
                            pwi.description = description;
                        }
                    });
                });
            });
            // @ts-ignore TODO: MATRIX-6934: nullStrictCheck should be fixed for next line
        } else if (this.config.postReduction || (this.isPrint && this.settings.parameter.forceAfterWeightsInPrint)) {
            // in case (this.isPrint && this.settings.parameter.forceAfterWeightsInPrint) make sure all weights are shown
            // @ts-ignore TODO: MATRIX-6934: nullStrictCheck should be fixed for next line
            if (this.isPrint && this.settings.parameter.forceAfterWeightsInPrint) {
                if (!this.config.postReduction) {
                    this.config.postReduction = { weights: [] };
                }
                if (!this.config.postReduction.weights) {
                    this.config.postReduction.weights = [];
                }
                // @ts-ignore TODO: MATRIX-6934: nullStrictCheck should be fixed for next line
                for (let factor of this.config.factors) {
                    for (let weight of factor.weights) {
                        // TODO: MATRIX-7555: lint errors should be fixed for next line
                        // eslint-disable-next-line
                        // TODO: MATRIX-7555: lint errors should be fixed for next line
                        // eslint-disable-next-line
                        if (this.config.postReduction.weights.filter((w) => w.type == weight.type).length == 0) {
                            this.config.postReduction.weights.push(weight);
                        }
                    }
                }
            }

            // *********************************************************************************
            // show post mitigation factors as normal UI (if wanted)
            // *********************************************************************************

            // initialize 'place to store the selected post reduction values if necessary
            if (!this.riskCalculator.getValue().postWeights) {
                this.riskCalculator.getValue().postWeights = [];
            }

            // @ts-ignore TODO: MATRIX-6934: nullStrictCheck should be fixed for next line
            // @ts-ignore TODO: MATRIX-6934: nullStrictCheck should be fixed for next line
            let help = this.config.postReduction.help ? this.config.postReduction.help : "Risk After Risk Controls";
            this.risk.append(
                $("<tr>").append($("<td colspan='4'>").html("<span class='baseControlHelp'>" + help + "</span>")),
            );

            // @ts-ignore TODO: MATRIX-6934: nullStrictCheck should be fixed for next line
            $.each(this.config.postReduction.weights, (weightIdx, weight) => {
                let riskPostRow = $(
                    // @ts-ignore TODO: MATRIX-6934: nullStrictCheck should be fixed for next line
                    '<tr class="riskPostRow" rowspan="' + that.config.postReduction.weights.length + '">',
                ).appendTo(that.risk);
                if (this.shouldUseOldLayout()) {
                    riskPostRow.append($("<td colspan='2'>"));
                } else {
                    riskPostRow.append($("<td colspan='1'>"));
                }
                that.renderWeight("After Risk Reduction", riskPostRow, false, weight);
                let select = $("select", riskPostRow);
                select.change(function () {
                    // store the new value
                    let exists = false;
                    let selectedOption = $(":selected", select);
                    let description = select.val();
                    let value = <number>selectedOption.data("value");
                    $.each(that.riskCalculator.getValue().postWeights, function (pwidx, pwi) {
                        if (pwi.type === weight.type) {
                            exists = true;
                            pwi.description = description;
                            pwi.value = value;
                        }
                    });
                    if (!exists) {
                        // @ts-ignore TODO: MATRIX-6934: nullStrictCheck should be fixed for next line
                        that.riskCalculator.getValue().postWeights.push({
                            type: weight.type,
                            value: value,
                            description: description,
                            label: weight.label,
                        });
                    }
                    // update the display
                    that.riskChange();
                });

                // default value for post mitigation is the before mitigation
                let sameBefore = $('select[name="' + weight.type + '"]', that._root);
                let selectValue = <number>$(":selected", sameBefore).data("value");

                // check if actual value has been saved before and overwrite the default
                $.each(that.riskCalculator.getValue().postWeights, function (pwidx, pwi) {
                    if (pwi.type === weight.type) {
                        selectValue = pwi.value;
                    }
                });

                $("option", select).each(function (optIdx, opt) {
                    // TODO: MATRIX-7555: lint errors should be fixed for next line
                    // eslint-disable-next-line
                    if ($(opt).data("value") == selectValue) {
                        $(opt).prop("selected", true);
                    }
                });

                // update the description text to match the dropdown
                $.each(that.riskCalculator.getValue().postWeights, function (pwidx, pwi) {
                    if (pwi.type === weight.type) {
                        pwi.description = select.val();
                    }
                });
            });
        }

        // *********************************************************************************
        // update all the maths once -> can trigger save, e.g. if configuration changed
        // *********************************************************************************

        this.riskChange();

        // try to cope with ui which converts simple dashes into unicode dashes....
        // @ts-ignore TODO: MATRIX-6934: nullStrictCheck should be fixed for next line
        if (this.hasChangedAsync()) {
            let oo = this._root.data("original");
            let no = JSON.stringify(this.riskCalculator.getValue());

            let same = "";
            for (let idx = 0; idx < Math.min(oo.length, no.length); idx++) {
                if (oo[idx] === no[idx]) {
                    same += oo[idx];
                } else {
                    //console.log(same);
                    //console.log("Difference at " + idx + " got: '" + oo[idx] + "' should have been '" + no[idx] + "'");
                    same = "";
                }
            }
            if (oo.length !== no.length) {
                //console.log("also object have not same length");
            }
        }

        if (!this.settings.canEdit) {
            $("input", this._root).attr("disabled", "disabled");
            $("select", this._root).attr("disabled", "disabled").css("background", "transparent");
        }

        if (this.settings.isForm || this.isPrint || this.settings.controlState === ControlState.Tooltip) {
            if (
                !(this.settings.parameter as IRiskTableParams).tableOptions &&
                !(this.config.ram && this.config.ram.hidden)
            ) {
                let tr = $("<tr>").appendTo(this.risk);
                if (this.shouldUseOldLayout()) {
                    tr.append($("<td>").attr("colspan", 2));

                    tr.append(
                        $('<td class="RAM riskReductionLabel" >').html(
                            this.config.ram && this.config.ram.short ? this.config.ram.short : "RAM",
                        ),
                    );
                    tr.append($('<td class="RAM  totalRAM risksum riskSelect ">'));
                } else {
                    tr.append($("<td colspan='1'>"));
                    let td = $("<td class='riskSelect'>").attr("colspan", 2);
                    tr.append(td);
                    td.append(
                        $("<span class='RAM riskReductionLabel'>").html(
                            this.config.ram && this.config.ram.short ? this.config.ram.short : "RAM",
                        ),
                    );
                    td.append($('<div class="RAM  totalRAM risksum riskSelect ">'));
                }

                this.riskChange();
            }
        }

        if (this.settings.isForm || this.settings.controlState === ControlState.DialogCreate) {
            let rmHelpKeys: string[] = [];
            $.each(this.config.factors, function (factorIdx, factor) {
                $.each(factor.weights, function (weightIdx, factor) {
                    rmHelpKeys.push(factor.label);
                });
            });

            $(".RBM", this._root).popover({
                container: "body",
                html: true,
                placement: "auto",
                title: this.config.rbm && this.config.rbm.long ? this.config.rbm.long : "RBM - Risk Before Mitigation",
                trigger: "hover",
                content: "<p>Formula: <b>" + rmHelpKeys.join(" " + this.config.method + " ") + "</b></p>",
            });
            $(".RAM", this._root).popover({
                container: "body",
                html: true,
                placement: "auto",
                title: this.config.ram && this.config.ram.long ? this.config.ram.long : "RAM - Risk After Mitigation",
                trigger: "hover",
                content: "<p>Formula: <b>" + rmHelpKeys.join(" " + this.config.method + " ") + "</b></p>",
            });
        }

        let oldLayout = true;
        /***********************************************************************************************
         * add special code for new printing
         ******************************************************************************************/
        if (this.isPrint) {
            // replace the input fields with spans for printing
            $.each($("input", this._root), function (idx, y) {
                let el = $(y);
                let classes: string[] = [el.attr("name"), "risk_factor"];
                let val = el.val() ? el.val() : "";
                // @ts-ignore TODO: MATRIX-6934: nullStrictCheck should be fixed for next line
                let pre = that.settings.parameter.showAttributeNames
                    ? `<div class="debug">${el.attr("name")}</div>` + val
                    : "";

                // @ts-ignore TODO: MATRIX-6934: nullStrictCheck should be fixed for next line
                el.replaceWith($(pre + `<span class="${classes.join(" ")}">${ml.UI.lt.forDB(val, null)}</span>`));
            });
            $.each($("textarea", this._root), function (idx, y) {
                let el = $(y);
                let classes: string[] = [el.attr("name"), "risk_factor"];
                let val = el.val() ? el.val() : "";
                // @ts-ignore TODO: MATRIX-6934: nullStrictCheck should be fixed for next line
                let pre = that.settings.parameter.showAttributeNames
                    ? `<div class="debug">${el.attr("name")}</div>`
                    : "";

                el.replaceWith(
                    $(
                        pre +
                            `<div style="white-space: pre-wrap;" class="${classes.join(" ")}">${ml.UI.lt.forDB(
                                val,
                                // @ts-ignore TODO: MATRIX-6934: nullStrictCheck should be fixed for next line
                                null,
                            )}</div>`,
                    ),
                );
            });
            $.each($(".riskrichtext", this._root), function (idx, y) {
                $(y).css("border", "none").css("height", "100%"); // no border around the right text
                // @ts-ignore TODO: MATRIX-6934: nullStrictCheck should be fixed for next line
                if (that.settings.parameter.showAttributeNames) {
                    $(y).prepend(`<div class="debug">${$(y).attr("name")}</div>`);
                } else {
                    $(y).addClass($(y).attr("name"));
                }
            });

            $.each($("select", this._root), function (idx, y) {
                let el = $(y);
                let val = $("option:selected", el).text();
                if (!val) {
                    val = "";
                }
                let classes: string[] = [el.attr("name")];
                if (el.hasClass("risk-control")) {
                    classes.push("risk_control");
                }
                if (el.hasClass("riskTextSelect")) {
                    // drop down for a risk factor
                    classes.push("risk_factor");
                    // @ts-ignore TODO: MATRIX-6934: nullStrictCheck should be fixed for next line
                    if (that.settings.parameter.showAttributeNames) {
                        val = `<div class="debug">${el.attr("name")}</div>` + val;
                    }
                }
                if (el.hasClass("riskSelect")) {
                    // drop down for a risk weight
                    classes.push("risk_weight");
                    // TODO: MATRIX-7555: lint errors should be fixed for next line
                    // eslint-disable-next-line
                    let isbeforeControls = el.closest(".riskPostRow").length == 0;
                    classes.push(isbeforeControls ? "before" : "after");

                    // @ts-ignore TODO: MATRIX-6934: nullStrictCheck should be fixed for next line
                    if (that.settings.parameter.showAttributeNames) {
                        val =
                            `<div class="debug">${isbeforeControls ? "before." : "after."}${el.attr("name")}</div>` +
                            val;
                    }
                }

                el.replaceWith($(`<span class="${classes.join(" ")}">${val}</span>`));
            });
            $.each($(".refId", this._root), function (idx, y) {
                let el = $(y);
                // create a fake element to create a hyper link with standard print settings
                el.replaceWith($(`<span class="_createHyper">${el.text()}</span>`));
            });
            $(".baseControl", this._root).addClass("riskControls");
            // @ts-ignore TODO: MATRIX-6934: nullStrictCheck should be fixed for next line
            if (that.settings.parameter.showAttributeNames) {
                $(".simpleMitigationTable", this._root).prepend(`<div class="debug">riskControls</div>`);
                $(".baseControl", this._root).prepend(`<div class="debug">riskControls</div>`);
                $(".totalRBM", this._root).prepend(`<div class="debug">totalRBM</div>`);
                $(".totalRAM", this._root).prepend(`<div class="debug">totalRAM</div>`);
            }
            $(".refTitle", this._root).remove();
        }
        for (let select of this.factorInputToTriggerChangeAfterLoad) {
            select.trigger("change");
        }
    }
    // TODO: MATRIX-7555: lint errors should be fixed for next line
    // eslint-disable-next-line
    async hasChangedAsync() {
        return (
            this.mitigationsRemoved || this._root.data("original") !== JSON.stringify(this.riskCalculator.getValue())
        );
    }
    // TODO: MATRIX-7555: lint errors should be fixed for next line
    // eslint-disable-next-line
    async getValueAsync() {
        let that = this;

        return JSON.stringify(that.riskCalculator.getValue());
    }
    // TODO: MATRIX-7555: lint errors should be fixed for next line
    // eslint-disable-next-line
    destroy() {
        $(".popover").remove();
    }
    // TODO: MATRIX-7555: lint errors should be fixed for next line
    // eslint-disable-next-line
    // TODO: MATRIX-7555: lint errors should be fixed for next line
    // eslint-disable-next-line
    resizeItem() {}

    // TODO: MATRIX-7555: lint errors should be fixed for next line
    // eslint-disable-next-line
    private syncTheLinks(actual: IRiskValueMitigation[]) {
        let that = this;

        let res = $.Deferred();

        // only need to do this if this is a mitigation table, otherwise links are created on the fly
        if (!this.config.mitigationTable) {
            res.resolve();
            return res;
        }

        let neededLinks = actual
            ? actual.map(function (mit) {
                  return mit.to;
              })
            : [];
        let existingLinks = this.settings.links
            ? this.settings.links.map(function (mit) {
                  return mit.to;
              })
            : [];
        let needToAdd = neededLinks.filter(function (link) {
            // TODO: MATRIX-7555: lint errors should be fixed for next line
            // eslint-disable-next-line
            return existingLinks.indexOf(link) == -1;
        });
        let needToRemove = existingLinks.filter(function (link) {
            // TODO: MATRIX-7555: lint errors should be fixed for next line
            // eslint-disable-next-line
            return neededLinks.indexOf(link) == -1;
        });
        // TODO: MATRIX-7555: lint errors should be fixed for next line
        // eslint-disable-next-line
        // TODO: MATRIX-7555: lint errors should be fixed for next line
        // eslint-disable-next-line
        if (needToAdd.length == 0 && needToRemove.length == 0) {
            res.resolve();
        } else {
            // @ts-ignore TODO: MATRIX-6934: nullStrictCheck should be fixed for next line
            let cadd: IReferenceChange[] = needToAdd.map(function (toId) {
                return { action: "addLink", fromId: that.settings.id, toId: toId };
            });
            // @ts-ignore TODO: MATRIX-6934: nullStrictCheck should be fixed for next line
            let crem: IReferenceChange[] = needToRemove.map(function (toId) {
                return { action: "removeLink", fromId: that.settings.id, toId: toId };
            });
            let changes = cadd.concat(crem);
            app.commitChangeListAsync(changes).always(function (error, stepsDone) {
                if (error) {
                    ml.UI.showError(error, "cancelled operation");
                    res.reject();
                } else {
                    // update the links which are stored with the item
                    $.each(needToAdd, function (cidx, c) {
                        // @ts-ignore TODO: MATRIX-6934: nullStrictCheck should be fixed for next line
                        // TODO: MATRIX-7555: lint errors should be fixed for next line
                        // eslint-disable-next-line
                        that.settings.links.push(<any>{ to: c });
                    });
                    $.each(needToRemove, function (cidx, c) {
                        // @ts-ignore TODO: MATRIX-6934: nullStrictCheck should be fixed for next line
                        that.settings.links = that.settings.links.filter(function (l) {
                            // TODO: MATRIX-7555: lint errors should be fixed for next line
                            // eslint-disable-next-line
                            return l.to != c;
                        });
                    });

                    res.resolve();
                }
            });
        }
        return res;
    }
    private controlsFromTable(riskControlsAsTable: string): IRiskValueMitigation[] {
        let that = this;

        if (!riskControlsAsTable) {
            return [];
        }
        let riskControls: IRiskValueMitigation[] = [];
        let table = JSON.parse(riskControlsAsTable);
        $.each(table, function (rcIdx, rcRow) {
            let exists = riskControls.filter(function (rc) {
                // TODO: MATRIX-7555: lint errors should be fixed for next line
                // eslint-disable-next-line
                return rc.to == rcRow.to;
            });
            let changes: IRiskValueMitigationChange[] = [];
            // TODO: MATRIX-7555: lint errors should be fixed for next line
            // eslint-disable-next-line
            if (exists.length == 1) {
                changes = exists[0].changes || [];
            }

            let added = false;
            if (rcRow.reduction) {
                $.each(that.config.reductions, function (reductionIdx, reduction) {
                    $.each(reduction.options, function (optionIdx, option) {
                        // TODO: MATRIX-7555: lint errors should be fixed for next line
                        // eslint-disable-next-line
                        if (rcRow.reduction == option.changes + "|" + option.by) {
                            changes.push({
                                by: option.by,
                                changes: option.changes,
                                description: option.shortname,
                                name: "00",
                            });
                            added = true;
                        }
                    });
                });
            }
            if (!added) {
                // maybe not selected, maybe not defined
                // TODO: MATRIX-7555: lint errors should be fixed for next line
                // eslint-disable-next-line
                changes.push(<any>{});
            }
            // add additional columns
            $.each(rcRow, function (key: string, val: string) {
                // TODO: MATRIX-7555: lint errors should be fixed for next line
                // eslint-disable-next-line
                // TODO: MATRIX-7555: lint errors should be fixed for next line
                // eslint-disable-next-line
                if (key != "to" && key != "reduction") {
                    // TODO: MATRIX-7555: lint errors should be fixed for next line
                    // eslint-disable-next-line
                    (<any>changes)[changes.length - 1][key] = val;
                }
            });
            // TODO: MATRIX-7555: lint errors should be fixed for next line
            // eslint-disable-next-line
            if (exists.length != 1) {
                let newMit: IRiskValueMitigation = {
                    to: rcRow.to,
                    title: app.getItemTitle(rcRow.to),
                    changes: changes,
                };
                riskControls.push(newMit);
            }
        });
        return riskControls;
    }

    private controlsToTable(riskControls: IRiskValueMitigation[]): string {
        // TODO: MATRIX-7555: lint errors should be fixed for next line
        // eslint-disable-next-line
        let table: any = [];
        $.each(riskControls, function (rcIdx, rc) {
            if (rc.changes && rc.changes.length > 0) {
                $.each(rc.changes, function (idx, cg) {
                    // TODO: MATRIX-7555: lint errors should be fixed for next line
                    // eslint-disable-next-line
                    let rcLine: any = {};
                    rcLine.to = rc.to;
                    rcLine.reduction = cg.changes + "|" + cg.by;
                    $.each(cg, function (key, val) {
                        // TODO: MATRIX-7555: lint errors should be fixed for next line
                        // eslint-disable-next-line
                        // TODO: MATRIX-7555: lint errors should be fixed for next line
                        // eslint-disable-next-line
                        // TODO: MATRIX-7555: lint errors should be fixed for next line
                        // eslint-disable-next-line
                        // TODO: MATRIX-7555: lint errors should be fixed for next line
                        // eslint-disable-next-line
                        if (key != "to" && key != "title" && key != "by" && key != "changes") {
                            rcLine[key] = val;
                        }
                    });
                    table.push(rcLine);
                });
            } else {
                // TODO: MATRIX-7555: lint errors should be fixed for next line
                // eslint-disable-next-line
                let rcLine: any = {};
                rcLine.to = rc.to;
                table.push(rcLine);
            }
        });
        return JSON.stringify(table);
    }

    // TODO: MATRIX-7555: lint errors should be fixed for next line
    // eslint-disable-next-line
    private renderFactorWithWeightsLine(factor: IRiskConfigFactor) {
        // Render one line in table
        let riskInputLine = $("<tr>").appendTo(this.risk);

        // Rowspan is the number of rows for label and input of the weight text (depends on number of associated factors)
        const rowspan = Math.max(1, factor.weights.length);

        if (this.shouldUseOldLayout()) {
            const tdLabel = $('<td class="risktd riskInputLabel">').attr("rowspan", rowspan);
            if (!factor.hideTextInput) {
                tdLabel.html(factor.label);
            }

            // First cell is the label and the input
            riskInputLine.append(tdLabel);
        }

        // If there are no factors, the weight input can be larger
        let colspan = (!factor.weights || factor.weights.length === 0) && factor.spancols ? 3 : 1;

        // Second cell is the input (a text field, dropdown, ...)
        const ti = this.renderFactorInput(factor);

        if (this.shouldUseOldLayout()) {
            riskInputLine.append($('<td class="risktd">').attr("colspan", colspan).attr("rowspan", rowspan).append(ti));
        } else {
            let td = $('<td class="risktd">').attr("colspan", colspan).attr("rowspan", rowspan).append(ti);
            let label = $('<span class="riskReductionLabel">');
            td.prepend(label);
            if (!factor.hideTextInput) {
                label.html(factor.label);
            }
            riskInputLine.append(td);
        }
        if (colspan === 1) {
            // Add additional columns for the risk factors. Each factor after the second will add a new line
            if (factor.weights.length === 0) {
                // Add two empty columns
                riskInputLine.append($("<td colspan='2'>"));
            } else {
                factor.weights.forEach((weight, weightIdx) => {
                    riskInputLine = this.renderWeight(factor.label, riskInputLine, weightIdx !== 0, weight);
                });
            }
        }
    }
    // TODO: MATRIX-7555: lint errors should be fixed for next line
    // eslint-disable-next-line
    private editRichText(field: JQuery) {
        let that = this;

        let dlg = $("#editFieldDlg");
        dlg.html("");
        dlg.addClass("dlg-no-scroll");
        dlg.removeClass("dlg-v-scroll");

        let rte = $("<div>");
        dlg.append($("<div>").append(rte));

        let padding = 28;

        ml.UI.showDialog(
            $("#editFieldDlg"),
            "Edit Factor",
            rte,
            730,
            550,
            [
                {
                    text: "OK",
                    class: "btnDoIt",
                    // TODO: MATRIX-7555: lint errors should be fixed for next line
                    // eslint-disable-next-line
                    click: async function () {
                        that.setFactorRichValue(field, await rte.getController().getValueAsync());
                        that.riskChange();
                        dlg.dialog("close");
                    },
                },
                {
                    text: "Cancel",
                    class: "btnCancelIt",
                    // TODO: MATRIX-7555: lint errors should be fixed for next line
                    // eslint-disable-next-line
                    click: function () {
                        dlg.dialog("close");
                    },
                },
            ],
            UIToolsConstants.Scroll.None,
            false,
            false,
            // @ts-ignore TODO: MATRIX-6934: nullStrictCheck should be fixed for next line
            null,
            () => {
                rte.richText({
                    controlState: ControlState.DialogEdit,
                    fieldValue: field.data("realValue"),
                    canEdit: true,
                    help: " ",
                    parameter: { height: 315, tableMode: false, autoEdit: true, autoFocus: true },
                });

                padding = $("#editFieldDlg").parent().width() - $("#editFieldDlg").width();
                let el = $(".note-editable", rte);
                el.on("keydown", async function (event) {
                    if (globalMatrix.globalShiftDown && event.keyCode === 13) {
                        if (event.preventDefault) {
                            event.preventDefault();
                        }
                        if (event.stopPropagation) {
                            event.stopPropagation();
                        }
                        that.setFactorRichValue(field, await rte.getController().getValueAsync());
                        dlg.dialog("close");
                    }
                });
            },
            () => {
                $(".note-editable", rte).height(dlg.height() - 65);
                $("#editFieldDlg").width($("#editFieldDlg").parent().width() - padding);
            },
            true,
        );
    }
    /* MATRIX-7924 : Cleanup will happen during save. No need to clean when setting the value. */
    private setFactorRichValue(field: JQuery, html: string): void {
        field.data("realValue", html);
        html = ml.SmartText.replaceTextFragments(html, true);
        field.html(html);
        ml.SmartText.showTooltips(field);
        field.highlightReferences();
    }

    private renderWeight(
        factorLabel: string,
        riskInputLine: JQuery,
        additionalRow: boolean,
        weight: IRiskConfigFactorWeight,
    ): JQuery {
        // add more columns (and new lines if needed with dropdowns like Probability 1 \/
        if (additionalRow) {
            riskInputLine = $("<tr>").appendTo(this.risk);
        }

        // create the label
        let weightLabel = $('<span class ="riskReductionLabel">').html(weight.label);
        if (this.shouldUseOldLayout()) {
            riskInputLine.append($("<td>").append(weightLabel));
        }

        if (weight.help && !this.isPrint) {
            // add help for label
            let help = $("<div>");
            let helpTable = $("<table class='table-bordered table-help'>").appendTo(help);
            let helpBody = $("<tbody>").appendTo(helpTable);
            helpTable.append(
                "<thead><tr><td class='riskhelpleft'>Value</td><td class='riskhelpright'>Description</td></tr></thead>",
            );

            for (const val of weight.values) {
                const ftext = RiskCalculator.labelDisplay(val); // val.factor + (val.shortname ? (" - " + val.shortname) : "");
                helpBody.append(
                    $("<tr>")
                        .append("<td class='riskhelpleft'>" + ftext + "</td>")
                        .append("<td class='riskhelpright'>" + val.help + "</td>"),
                );
            }
            weightLabel.popover({
                template:
                    '<div class="popover riskhelppopover" role="tooltip"><div class="arrow"></div><h3 class="popover-title"></h3><div class="popover-content"><div class="data-content"></div></div></div>',
                container: "body",
                html: true,
                placement: "auto",
                title: factorLabel + " - " + weight.label,
                trigger: "hover",
                content: help,
            });
        }

        // create the weight
        let weightSelect = this.renderWeightInput(weight, true, false); // default true: can edit, false: not a table
        let weightTd = $("<td>").append(weightSelect);
        if (!this.shouldUseOldLayout()) {
            weightTd.prepend(weightLabel);
        }
        if (weight.hidden) {
            weightTd.addClass("hidden");
        }
        riskInputLine.append(weightTd);

        return riskInputLine;
    }

    // TODO: MATRIX-7555: lint errors should be fixed for next line
    // eslint-disable-next-line
    // TODO: MATRIX-7555: lint errors should be fixed for next line
    // eslint-disable-next-line
    private setWeight(factorType: string, weightType: string, val: number, label: string, description: string) {
        $.each(this.riskCalculator.getValue().factors, function (factorIdx, factor) {
            if (factor.type === factorType) {
                let found = false;
                for (let idx = 0; idx < factor.weights.length; idx++) {
                    if (factor.weights[idx].type === weightType) {
                        factor.weights[idx].value = val;
                        factor.weights[idx].description = description;
                        found = true;
                    }
                }
                if (!found) {
                    factor.weights.push({
                        type: weightType,
                        value: val,
                        label: label,
                        description: description,
                    });
                }
            }
        });
    }

    // TODO: MATRIX-7555: lint errors should be fixed for next line
    // eslint-disable-next-line
    private setFactor(type: string, label: string, val: string, inputType: string) {
        for (let idx = 0; idx < this.riskCalculator.getValue().factors.length; idx++) {
            if (this.riskCalculator.getValue().factors[idx].type === type) {
                this.riskCalculator.getValue().factors[idx].value = val;
                return;
            }
        }
        let fac: IRiskValueFactor = {
            type: type,
            value: val,
            label: label,
            weights: [],
        };
        if (inputType) {
            fac.inputType = inputType;
        }
        this.riskCalculator.getValue().factors.push(fac);
    }

    // called from once after painting or UI in case any value changes: this updates the math and the save state
    // TODO: MATRIX-7555: lint errors should be fixed for next line
    // eslint-disable-next-line
    riskChange() {
        let that = this;
        // read and calculate risk values after mitigations. save them in item
        // TODO: MATRIX-7555: lint errors should be fixed for next line
        // eslint-disable-next-line
        function savePerMitigationEffect() {
            // TODO: MATRIX-7555: lint errors should be fixed for next line
            // eslint-disable-next-line
            // TODO: MATRIX-7555: lint errors should be fixed for next line
            // eslint-disable-next-line
            function setChange(
                mitigation: IRiskValueMitigation,
                name: string,
                by: number,
                changes: string,
                description: string,
            ) {
                mitigation.changes = mitigation.changes || [];

                for (let idx = 0; idx < mitigation.changes.length; idx++) {
                    if (mitigation.changes[idx].name === name) {
                        mitigation.changes[idx].by = by;
                        mitigation.changes[idx].changes = changes ? changes : "";
                        mitigation.changes[idx].description = description;
                        return;
                    }
                }
                mitigation.changes.push({
                    name: name,
                    by: by,
                    changes: changes,
                    description: description,
                });
            }

            $.each(that.riskCalculator.getValue().mitigations, function (mitigationIdx, mitigation) {
                // get the unique id of the mitigation: the target item
                let mitigationTo = mitigation.to;
                if (that.config.mitigationTable) {
                } else {
                    // get all related mitigation controls, by the id of the item they relate to
                    let mitigationControls = $(".mitigation" + mitigationTo, that._root);
                    // sum up all values for the mitigation
                    // in case there are several drop downs sum up all values
                    $.each(mitigationControls, function (mitigationControlIdx, mitigationControl) {
                        let selectedOption = $(":selected", mitigationControl);
                        let mitigationName = $(mitigationControl).attr("name");
                        let changes = selectedOption.data("changes");
                        let by = selectedOption.data("value");

                        // save the mitgation value which is set in the UI
                        setChange(mitigation, mitigationName, by, changes, selectedOption.text());
                    });
                }
            });
        }
        // use whatever user selected after mitigations
        // TODO: MATRIX-7555: lint errors should be fixed for next line
        // eslint-disable-next-line
        function saveCombinedMitgationValues(rbm: IRiskValueMap) {
            // @ts-ignore TODO: MATRIX-6934: nullStrictCheck should be fixed for next line
            if (that.riskCalculator.getValue().mitigations.length === 0 && that.config.mitigationTypes.length > 0) {
                if ((<IRiskTableParams>that.settings.parameter).tableOptions) {
                    $.each($("select.riskAfter", that._root), function (weightIdx, weight) {
                        let name = $(weight).attr("name");
                        // TODO: MATRIX-7555: lint errors should be fixed for next line
                        // eslint-disable-next-line
                        that.setSelectValue($(weight), <any>rbm[name]);
                    });
                    $(".riskAfter.riskSelect.riskInputValue", that._root).attr("disabled", "disabled");
                } else {
                    $(".riskPostRow", that._root).addClass("hidden");
                }
                // no mitigations selected -> nothing can change -> use original values
                return;
            }
            let riskPostRowsWeights: JQuery;
            if ((<IRiskTableParams>that.settings.parameter).tableOptions) {
                $(".riskAfter.riskSelect.riskInputValue", that._root).removeAttr("disabled");
                // update the readonly selects
                let readonlyWeights = $("select.riskAfter.riskSelect.riskInputValuedisabled", that._root);
                $.each(readonlyWeights, function (weightIdx, weight) {
                    let name = $(weight).attr("name");
                    // TODO: MATRIX-7555: lint errors should be fixed for next line
                    // eslint-disable-next-line
                    that.setSelectValue($(weight), <any>rbm[name]);
                });
                // get the values
                riskPostRowsWeights = $("select.riskAfter.riskSelect option:selected", that._root);
            } else {
                $(".riskPostRow").show();
                riskPostRowsWeights = $(".riskPostRow select  option:selected", that._root);
            }
        }
        // $ to display a risk level before and after mitigation including the math
        // TODO: MATRIX-7555: lint errors should be fixed for next line
        // eslint-disable-next-line
        function updateRiskDisplay(ctrl: JQuery, riskValues: IRiskValueMap) {
            let riskText = that.riskCalculator.getRiskSumText(riskValues);

            if (that.config.method === "lookup") {
                ctrl.html(riskText.text);
                ctrl.css("background-color", riskText.backgroundColor);
                ctrl.css("color", riskText.foregroundColor);
            } else {
                ctrl.html(riskText.text);
                ctrl.removeClass("riskgreen").removeClass("riskyellow").removeClass("riskred").addClass(riskText.css);
            }
        }

        // something in ui changed, read all values and update math
        $.each(this.config.factors, function (factorIdx, factor) {
            // get from UI and set in results the input text (e.g. in Cause field
            let inp: string;
            if (factor.inputType && factor.inputType === "select") {
                inp = $("select[name=" + factor.type + "]", that._root).val();
            } else if (factor.inputType && factor.inputType === "textarea") {
                // @ts-ignore TODO: MATRIX-6934: nullStrictCheck should be fixed for next line
                inp = ml.UI.lt.forDB($("textarea[name=" + factor.type + "]", that._root).val(), that.settings.fieldId);
            } else if (factor.inputType && factor.inputType === "richtext") {
                inp = $(".riskrichtext[name=" + factor.type + "]", that._root).data("realValue");
            } else {
                // @ts-ignore TODO: MATRIX-6934: nullStrictCheck should be fixed for next line
                inp = ml.UI.lt.forDB($("input[name=" + factor.type + "]", that._root).val(), that.settings.fieldId);
            }
            that.setFactor(factor.type, factor.label, inp, factor.inputType);
            // get from UI and set in results the weights
            $.each(factor.weights, function (weightIdx, weight) {
                let selectedOption = $("select[name=" + weight.type + "] :selected:first()", that._root);
                that.setWeight(
                    factor.type,
                    weight.type,
                    selectedOption.data("value"),
                    weight.label,
                    // @ts-ignore TODO: MATRIX-6934: nullStrictCheck should be fixed for next line
                    ml.UI.lt.forDB(selectedOption.data("description"), that.settings.fieldId),
                );
            });
        });

        let rbm = this.riskCalculator.getRBM();

        updateRiskDisplay($(".totalRBM", this._root), rbm);

        // update the post reduction risk ui and read the values
        if (this.config.postReduction) {
            // use whatever user selected after mitigations
            try {
                // still allow to have drop downs / even if they do not change final math.
                savePerMitigationEffect();
            } catch (ex) {}
            saveCombinedMitgationValues(rbm);
        } else {
            // substract something from values as defined in drop downs after each mitigation
            savePerMitigationEffect();
        }
        // calculate the ram
        let ramCalc = this.riskCalculator.getRAM(rbm);

        // in case we are in a table in a folder, also update the readonly columns
        if ((this.settings.parameter as IRiskTableParams).tableOptions) {
            $.each(this.config.factors, function (factorIdx, factor) {
                $.each(factor.weights, function (weightIdx, weight) {
                    let dd = $('select[name="' + weight.type + '"].riskInputValuedisabled.riskAfter', that._root);
                    that.setSelectValues(dd, ramCalc[weight.type]);
                });
            });
        }

        // update overall result
        updateRiskDisplay($(".totalRAM", this._root), ramCalc);

        if (this.settings.valueChanged) {
            this.settings.valueChanged.apply(null);
        }
    }

    // called if user added a new mitigation (or removed one)
    // TODO: MATRIX-7555: lint errors should be fixed for next line
    // eslint-disable-next-line
    private async mitigationChanged() {
        (await this.riskCalculator.getValue()).mitigations = this.config.mitigationTable
            ? this.controlsFromTable(await this.mitbody.getController().getValueAsync())
            : await this.mitbody.getController().getValueAsync();
        if (this.config.mitigationTable) {
            // TODO: MATRIX-7555: lint errors should be fixed for next line
            // eslint-disable-next-line
            this.syncTheLinks((await this.riskCalculator.getValue()).mitigations).done(function () {});
        }
        // update risk display if anything
        this.riskChange();

        // update main UI about change
        if (this.settings.valueChanged) {
            this.settings.valueChanged.apply(null);
        }
    }

    private getLabelFactor(type: string): string {
        if (this.config.factors) {
            for (let idx = 0; idx < this.config.factors.length; idx++) {
                // TODO: MATRIX-7555: lint errors should be fixed for next line
                // eslint-disable-next-line
                if (this.config.factors[idx].type == type) {
                    return this.config.factors[idx].label;
                }
            }
        }
        return "";
    }

    private getLabelWeight(type: string): string {
        if (this.config.factors) {
            for (let idx = 0; idx < this.config.factors.length; idx++) {
                for (let jdx = 0; jdx < this.config.factors[idx].weights.length; jdx++) {
                    // TODO: MATRIX-7555: lint errors should be fixed for next line
                    // eslint-disable-next-line
                    if (this.config.factors[idx].weights[jdx].type == type) {
                        return this.config.factors[idx].weights[jdx].label;
                    }
                }
            }
        }
        return "";
    }

    private getLabelWeightFactor(type: string, factor: number): string {
        if (this.config.factors) {
            for (const factorIt of this.config.factors) {
                if (factorIt.weights) {
                    for (const weight of factorIt.weights) {
                        // TODO: MATRIX-7555: lint errors should be fixed for next line
                        // eslint-disable-next-line
                        if (weight.type == type) {
                            if (weight.values) {
                                for (const value of weight.values) {
                                    // TODO: MATRIX-7555: lint errors should be fixed for next line
                                    // eslint-disable-next-line
                                    if (value.factor == factor) {
                                        return value.shortname;
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
        return "";
    }

    // TODO: MATRIX-7555: lint errors should be fixed for next line
    // eslint-disable-next-line
    private createMitigationSelect(
        reduction: IRiskConfigReduction,
        mitigationElement: IRiskValueMitigation,
        mitigationTo: string,
    ) {
        let that = this;

        let mit = $("<select class='risk-control multimitctrl riskInputValue' name='" + reduction.name + "'>");

        // mitigation can be changed, so add drop downs and other info
        // make sure mitigation can be found by it's id (in getRiskAfterMitigation)
        mit.addClass("mitigation" + mitigationTo);

        mit.on("change", function () {
            that.riskChange();
        });

        $.each(reduction.options, function (optionIdx, option) {
            let opt = $("<option>" + option.shortname + "</option>");

            opt.data("changes", option.changes);
            opt.data("value", option.by);
            //MATRIX-7446 : disabling coloring of risk reduction in red for positive value (increasing the risk factors)
            // opt.css("color", option.by > 0 ? "#F00" : "#000");
            mit.append(opt);
        });

        // make sure there is an array (for new mitigations)
        if (!mitigationElement.changes) {
            mitigationElement.changes = [];
        }

        // check if there is already a value
        let exists = false;
        $.each(mitigationElement.changes, function (changedIdx, change) {
            if (change.name === reduction.name) {
                exists = true;
                that.setSelectValues(mit, change.by, change.changes);
            }
        });
        if (!exists) {
            let description = reduction.options.length > 0 ? reduction.options[0].shortname : "";
            let by = reduction.options.length > 0 ? reduction.options[0].by : 0;

            mitigationElement.changes.push({ name: reduction.name, changes: "", description: description, by: by });
        }

        return mit;
    }

    // TODO: MATRIX-7555: lint errors should be fixed for next line
    // eslint-disable-next-line
    private mitigationRenderer(referenceLI: JQuery, mitigationTo: string, mitigationIdx: number) {
        let that = this;

        // get the saved data for the mitigation
        // @ts-ignore TODO: MATRIX-6934: nullStrictCheck should be fixed for next line
        let mitigationElement: IRiskValueMitigation = null;
        $.each(this.riskCalculator.getValue().mitigations, function (mitIdx, mit) {
            if (mit.to === mitigationTo) {
                mitigationElement = mit;
            }
        });

        // now build all the drop downs / the one drop down to mitigate the risk

        $.each(this.config.reductions, function (reductionIdx, reduction) {
            let mit = that.createMitigationSelect(reduction, mitigationElement, mitigationTo);
            referenceLI.append(mit);
        });
    }

    // set values of a select to closest value saved with item
    // TODO: MATRIX-7555: lint errors should be fixed for next line
    // eslint-disable-next-line
    private setSelectValues(selects: JQuery, val: number, impact?: string) {
        let that = this;

        $.each(selects, function (selectIdx, select) {
            that.setSelectValue(select, val, impact);
        });
    }
    // TODO: MATRIX-7555: lint errors should be fixed for next line
    // eslint-disable-next-line
    private setSelectValue(select: JQuery, val: number, impact?: string) {
        $($("option", select)[0]).prop("selected", true);
        if (!val) {
            return;
        }
        let difference = 10000000;
        $.each($("option", select), function (optionIdx, option) {
            if (!impact || $(option).data("changes") === impact) {
                let d = $(option).data("value") - val;
                if (d * d < difference) {
                    difference = d * d;
                    $(option).prop("selected", true);
                }
            }
        });
    }

    // TODO: MATRIX-7555: lint errors should be fixed for next line
    // eslint-disable-next-line
    private canBeMitigation(itemId: string) {
        // @ts-ignore TODO: MATRIX-6934: nullStrictCheck should be fixed for next line
        for (let mt = 0; mt < this.config.mitigationTypes.length; mt++) {
            // @ts-ignore TODO: MATRIX-6934: nullStrictCheck should be fixed for next line
            if (itemId.indexOf(this.config.mitigationTypes[mt].type + "-") === 0) {
                return true;
            }
        }
        return false;
    }

    /********************************
     * render as table control
     ********************************/

    // TODO: MATRIX-7555: lint errors should be fixed for next line
    // eslint-disable-next-line
    private renderTableBodyRow(riskId: string) {
        let that = this;

        let tr = this._root;

        // add column risk id
        if (riskId) {
            let itemIdCell = $("<td class='riskIdCell'>")
                .appendTo(tr)
                // @ts-ignore TODO: MATRIX-6934: nullStrictCheck should be fixed for next line
                .html(riskId + ((this.settings.parameter as IRiskTableParams).tableOptions.showFullRisk ? "!" : ""));
            if (this.settings.canEdit === false) {
                itemIdCell.append($("<span><i class='fa fa-lock'></i></span>"));
            }
        }

        // add columns for text inputs (weights)
        $.each(this.config.factors, function (factorIdx, factor) {
            let input = that.renderFactorInput(factor);
            input.addClass("riskBefore");
            let td = $("<td>");
            if (factor.hideTextInput === true) {
                td.addClass("hidden");
            }
            td.appendTo(tr).append(input);
        });

        // add columns values inputs (factors)
        $.each(this.config.factors, function (factorIdx, factor) {
            $.each(factor.weights, function (weightIdx, weight) {
                let input = that.renderWeightInput(weight, true, true);
                input.addClass("riskBefore");
                let td = $("<td>");
                td.appendTo(tr).append(input); // column for weight (drop down input);
                if (weight?.hidden === true) {
                    td.addClass("hidden");
                }
            });
        });
        // add column for RBM // RPN before controls
        if (!this.config?.rbm?.hidden) {
            $("<td class='riskBefore RBM riskReductionLabel totalRBM risksum riskSelect'>").appendTo(tr);
        }

        // container for selector
        this.mitbody = $("<div>");
        // add column for risk controls
        $("<td class='tinyRisks'>").appendTo(tr).append(this.mitbody);
        // add details
        this.renderMitigationSelect(true);

        // add columns for values (factors) after risk controls
        $.each(this.config.factors, function (factorIdx, factor) {
            $.each(factor.weights, function (weightIdx, weight) {
                let input = that.renderWeightInput(weight, !!that.config.postReduction, true);
                input.addClass("riskAfter");
                let td = $("<td>").appendTo(tr).append(input); // column for weight (drop down input);
                if (
                    weight.hidden === true ||
                    (that.settings.hideReadonlyColumns &&
                        (ml.JSON.isTrue(weight.readonly) || !that.config.postReduction))
                ) {
                    td.addClass("hidden");
                }
                if (that.config.postReduction) {
                    let visibleColumns = that.config.postReduction.weights
                        .filter((w) => !w.hidden)
                        .map(function (weight) {
                            return weight.type;
                        });
                    // TODO: MATRIX-7555: lint errors should be fixed for next line
                    // eslint-disable-next-line
                    if (visibleColumns.indexOf(weight.type) == -1) {
                        td.remove();
                    }
                }
            });
        });
        // column for RAM // RPN after  controls

        if (!this.config?.ram?.hidden) {
            $("<td class='riskAfter risksum RAM RAMValue riskSelect totalRAM'>").appendTo(tr);
        }

        return tr;
    }

    /********************************
     * user inputs
     ********************************/

    private renderFactorInput(factor: IRiskConfigFactor): JQuery {
        const isReadonly = ml.JSON.isTrue(factor.readonly);
        const fd = isReadonly ? "disabled" : "";
        const canEdit = this.settings.canEdit! && !isReadonly;

        let ti: JQuery;

        switch (factor.inputType) {
            case "select":
                ti = this.createSelectInput(factor, fd);
                this.attachFactorInputChangeEvent(ti, factor);
                break;
            case "textarea":
                ti = this.createTextareaInput(factor, fd, canEdit);
                break;
            case "richtext":
                ti = this.createRichtextInput(factor, canEdit);
                break;
            default:
                ti = this.createDefaultInput(factor, fd, canEdit);
                break;
        }

        if (factor.hideTextInput) {
            ti.addClass("hidden");
        }
        return ti;
    }

    private createSelectInput(factor: IRiskConfigFactor, fd: string): JQuery {
        const ti = $(
            '<select class="form-control riskInputValue riskTextSelect' + fd + '" style="width:100%" ' + fd + ">",
        )
            .attr("name", factor.type)
            .attr("placeholder", "Enter " + factor.label);
        if (factor.options) {
            factor.options.forEach((option) => {
                ti.append(
                    $("<option value='" + option.value + "'>")
                        .html(option.label)
                        .data("changes", option.changes),
                );
            });
        }
        return ti;
    }

    private createTextareaInput(factor: IRiskConfigFactor, fd: string, canEdit: boolean): JQuery {
        return $(
            `<textarea type="text" ${
                canEdit ? "" : "readonly "
            }class="form-control riskText${fd} riskInputTextArea riskTextArea" ${fd}>`,
        )
            .attr("name", factor.type)
            .attr("placeholder", "Enter " + factor.label);
    }

    private createRichtextInput(factor: IRiskConfigFactor, canEdit: boolean): JQuery {
        const ti = $('<div class="riskrichtext riskTextArea form-control">')
            .attr("name", factor.type)
            .attr("placeholder", "Enter " + factor.label);

        if (canEdit) {
            ti.click(() => this.editRichText(ti));
        }

        return ti;
    }

    private createDefaultInput(factor: IRiskConfigFactor, fd: string, canEdit: boolean): JQuery {
        return $(
            `<input autocomplete="off" type="text" ${
                canEdit ? "" : `readonly `
            } class="form-control riskTextSelectStyle riskText${fd}" ${fd}>`,
        )
            .attr("name", factor.type)
            .attr("placeholder", "Enter " + factor.label);
    }

    private attachFactorInputChangeEvent(ti: JQuery, factor: IRiskConfigFactor): void {
        this.factorInputToTriggerChangeAfterLoad.push(ti);
        ti.change((event: JQueryEventObject) => {
            const option = $("option:selected", $(event.delegateTarget));
            const changes = option.data("changes");
            if (changes) {
                changes.forEach((change: IRiskConfigSelectChanges) => {
                    if (change.changesWeight) {
                        this.setSelectValues(
                            $(
                                'tr:not(.riskPostRow) select[name="' + change.changesWeight + '"]:not(.riskPostRow)',
                                this._root,
                            ),
                            change.value as number,
                        );
                    }
                    if (change.changesFactor && this.config.factors) {
                        const fact = this.config.factors.filter(
                            (f) => f.type === change.changesFactor && f.inputType === "richtext",
                        );
                        if (fact.length === 1) {
                            this.setFactorRichValue(
                                $('tr:not(.riskPostRow) [name="' + change.changesFactor + '"]', this._root),
                                change.value as string,
                            );
                        } else {
                            $('tr:not(.riskPostRow)  [name="' + change.changesFactor + '"]', this._root).val(
                                change.value,
                            );
                        }
                    }
                });
            }
        });
    }

    // this enters the input for a weight dropdown
    // TODO: MATRIX-7555: lint errors should be fixed for next line
    // eslint-disable-next-line
    private renderWeightInput(weight: IRiskConfigFactorWeight, canEdit: boolean, forTable: boolean) {
        let disabled = "";
        let riskInputValue = "riskInputValue";
        if (!canEdit || ml.JSON.isTrue(weight.readonly)) {
            disabled = "disabled";
            riskInputValue = "riskInputValuedisabled";
        }
        let weightSelect = $('<select class="form-control riskSelect ' + riskInputValue + '" ' + disabled + ">").attr(
            "name",
            weight.type,
        );

        // add options for dropdown
        $.each(weight.values, function (valIdx, val) {
            let description = RiskCalculator.labelDisplay(val); // val.factor + (val.shortname? (" - " + val.shortname) : "");
            let text = forTable ? val.factor : description;
            weightSelect.append(
                $("<option>" + text + "</option>")
                    .data("value", val.factor)
                    .data("description", description),
            );
        });

        // hide it
        if (weight.hidden) {
            weightSelect.addClass("hidden");
        }
        return weightSelect;
    }
    // Replace highlight ref in richtext when reloading.
    // TODO: MATRIX-7555: lint errors should be fixed for next line
    // eslint-disable-next-line
    highlightReferences() {
        $(this._root).find(".riskrichtext").highlightReferences();
    }

    // render area to select mitigations
    // TODO: MATRIX-7555: lint errors should be fixed for next line
    // eslint-disable-next-line
    private renderMitigationSelect(forTable: boolean) {
        let that = this;

        // @ts-ignore TODO: MATRIX-6934: nullStrictCheck should be fixed for next line
        if (this.config.mitigationTypes.length > 0) {
            let theMitigations = this.riskCalculator.getValue().mitigations;
            if (theMitigations) {
                // fix the labels if they need fixing
                $.each(theMitigations, function (theIdx, theMitigation) {
                    $.each(theMitigation.changes, function (theJdx, theChange) {
                        $.each(that.config.reductions, function (idx, reduction) {
                            $.each(reduction.options, function (jdx, option) {
                                // TODO: MATRIX-7555: lint errors should be fixed for next line
                                // eslint-disable-next-line
                                // TODO: MATRIX-7555: lint errors should be fixed for next line
                                // eslint-disable-next-line
                                if (option.by == theChange.by && option.changes == theChange.changes) {
                                    theChange.description = option.shortname;
                                }
                            });
                        });
                    });
                });
            }

            if (this.config.mitigationTable) {
                let columns: ITableControlOptionsColumn[] = [];
                $.each(this.config.mitigationTable.columns, function (colIdx, colDef) {
                    switch (colDef.editor) {
                        case "control":
                            columns.push({
                                editor: ColumnEditor.category,
                                name: colDef.name,
                                field: "to",
                                // TODO: MATRIX-7555: lint errors should be fixed for next line
                                // eslint-disable-next-line
                                options: <any>{
                                    singleSelect: true,
                                    showTitle: true,
                                    // @ts-ignore TODO: MATRIX-6934: nullStrictCheck should be fixed for next line
                                    // TODO: MATRIX-7555: lint errors should be fixed for next line
                                    // eslint-disable-next-line
                                    categories: that.config.mitigationTypes.map(function (mt: any) {
                                        return mt.type;
                                    }),
                                },
                            });
                            break;
                        case "reduction": {
                            // TODO: MATRIX-7555: lint errors should be fixed for next line
                            // eslint-disable-next-line
                            let options: any = {};
                            $.each(that.config.reductions, function (reductionIdx, reduction) {
                                $.each(reduction.options, function (optionIdx, option) {
                                    options[option.changes + "|" + option.by] = option.shortname;
                                });
                            });
                            columns.push({
                                editor: ColumnEditor.select,
                                name: colDef.name,
                                field: "reduction",
                                options: options,
                            });
                            break;
                        }
                        default:
                            // take it as it is
                            columns.push({
                                editor: colDef.editor,
                                name: colDef.name,
                                field: colDef.field,
                                options: colDef.options,
                            });
                            break;
                    }
                });
                this.mitbody.width(forTable ? "" : this.risk.width());
                this.mitbody.tableCtrl({
                    canEdit: that.settings.canEdit,
                    help: that.config.controls ? that.config.controls : "Risk Controls",
                    parameter: {
                        canBeModified: that.settings.canEdit,
                        create: that.settings.canEdit,
                        showLineNumbers: that.settings.canEdit,
                        columns: columns,
                    },
                    fieldValue: that.controlsToTable(theMitigations),
                    controlState: this.settings.controlState,
                    id: this.settings.id,
                    valueChanged: async () => await that.mitigationChanged(),
                    item: this.settings.item,
                });
                // TODO: MATRIX-7555: lint errors should be fixed for next line
                // eslint-disable-next-line
                if (this.settings.controlState != ControlState.HistoryView) {
                    this.mitbody.highlightReferences();
                }
                this.mitbody.getController().redraw();
                // handle the special case of printing -> convert the mitigation table into a simple table
                if (this.isPrint) {
                    let simpleTable = $("<table class='simpleMitigationTable table table-bordered'>");
                    let head = $("<thead>").appendTo(simpleTable);
                    let body = $("<tbody>").appendTo(simpleTable);

                    $(".slick-header-column", this.mitbody).each(function (idx, col) {
                        if (idx) {
                            head.append("<th>" + $(col).text() + "</th>");
                        }
                    });
                    $(".slick-row", this.mitbody).each(function (rowIdx, row) {
                        let tr = $("<tr>").appendTo(body);
                        $(".slick-cell", $(row)).each(function (colIdx, col) {
                            if (colIdx) {
                                tr.append("<td>" + $(col).text() + "</td>");
                            }
                        });
                    });
                    this.mitbody.replaceWith(simpleTable);
                }
            } else {
                this.mitbody.linkCollection({
                    canEdit: that.settings.canEdit,
                    help: that.config.controls ? that.config.controls : "Risk Controls",
                    parameter: { linkTypes: this.config.mitigationTypes },
                    fieldValue: theMitigations,
                    mitigationRenderer: (referenceLI: JQuery, mitigationTo: string, mitigationIdx: number) =>
                        that.mitigationRenderer(referenceLI, mitigationTo, mitigationIdx),
                    controlState: this.settings.controlState,
                    id: this.settings.id,
                    valueChanged: async () => await that.mitigationChanged(),
                    item: this.settings.item,
                    tiny: forTable,
                });
            }
        }
    }

    private shouldUseOldLayout(): boolean {
        if ((this.settings.parameter as IRiskParameter)?.riskConfig?.legacyLayout === true) {
            return true;
        }
        if ((this.settings.parameter as IRiskTableParams).tableOptions) {
            return true;
        }

        switch (this.settings.controlState) {
            case ControlState.DialogCreate:
            case ControlState.FormView:
            case ControlState.FormEdit:
                return false;
            default:
                return true;
        }
    }
}
